import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
  } from "react";
  import axios from "axios";
  
  const PromotionsContext = createContext(null);
  
  const roundMoney = (value) =>
    Math.round((Number(value) + Number.EPSILON) * 100) / 100;
  
  const nowIsWithinRange = (promotion) => {
    const now = new Date();
  
    const startOk =
      !promotion.startDate ||
      new Date(promotion.startDate).getTime() <= now.getTime();
  
    const endOk =
      !promotion.endDate ||
      new Date(promotion.endDate).getTime() >= now.getTime();
  
    return startOk && endOk;
  };
  
  const isPromotionCurrentlyValid = (promotion) => {
    return Boolean(promotion?.isActive && nowIsWithinRange(promotion));
  };
  
  const sortPromotions = (promotions) => {
    return [...promotions].sort((a, b) => {
      const priorityA = Number(a.priority || 0);
      const priorityB = Number(b.priority || 0);
  
      if (priorityA !== priorityB) return priorityB - priorityA;
  
      const createdA = new Date(a.createdAt || 0).getTime();
      const createdB = new Date(b.createdAt || 0).getTime();
  
      return createdB - createdA;
    });
  };
  
  const normalizeCartItems = (cartItems) => {
    return (cartItems || []).map((item, index) => ({
      ...item,
      __cartLineId:
        item.__cartLineId ||
        `${item.SKU || "unknown"}-${index}-${item.productURI || "item"}`,
      quantity: Number(item.quantity || 1),
      price: Number(item.price || 0),
    }));
  };
  
  const promotionAppliesToProduct = (promotion, product) => {
    if (!promotion || !product) return false;
    if (!isPromotionCurrentlyValid(promotion)) return false;
  
    if (promotion.scopeType === "global") return true;
  
    if (promotion.scopeType === "products") {
      const sku = String(product.SKU);
      return Array.isArray(promotion.productSKUs)
        ? promotion.productSKUs.map(String).includes(sku)
        : false;
    }
  
    return false;
  };
  
  const buildLineItems = (cartItems) => {
    return normalizeCartItems(cartItems).map((item) => ({
      lineId: item.__cartLineId,
      item,
      quantity: item.quantity,
      basePrice: Number(item.price || 0),
      originalLineTotal: roundMoney(
        Number(item.price || 0) * Number(item.quantity || 1)
      ),
      discountedLineTotal: roundMoney(
        Number(item.price || 0) * Number(item.quantity || 1)
      ),
      lineDiscount: 0,
      appliedPromotions: [],
      promotionLabels: [],
    }));
  };
  
  const buildUnitsFromLineItems = (lineItems) => {
    const units = [];
  
    for (const line of lineItems) {
      for (let i = 0; i < line.quantity; i++) {
        units.push({
          unitId: `${line.lineId}__${i}`,
          lineId: line.lineId,
          item: line.item,
          originalPrice: line.basePrice,
          currentPrice: line.basePrice,
          lineDiscount: 0,
          appliedPromotions: [],
          promotionLabels: [],
          hasNonCombinablePromotion: false,
        });
      }
    }
  
    return units;
  };
  
  const clonePromotion = (promotion) => ({ ...promotion });
  
  const canUnitReceivePromotion = (unit, promotion) => {
    if (!promotionAppliesToProduct(promotion, unit.item)) return false;
  
    if (!promotion.canCombine && unit.hasNonCombinablePromotion) {
      return false;
    }
  
    return true;
  };
  
  const addDiscountToUnit = (unit, amount, promotion, label) => {
    const safeAmount = roundMoney(
      Math.max(0, Math.min(amount, unit.currentPrice))
    );
  
    if (safeAmount <= 0) return;
  
    unit.lineDiscount = roundMoney(unit.lineDiscount + safeAmount);
    unit.currentPrice = roundMoney(unit.currentPrice - safeAmount);
  
    if (
      promotion?._id &&
      !unit.appliedPromotions.find((p) => p._id === promotion._id)
    ) {
      unit.appliedPromotions.push(clonePromotion(promotion));
    }
  
    if (label && !unit.promotionLabels.includes(label)) {
      unit.promotionLabels.push(label);
    }
  
    if (!promotion.canCombine) {
      unit.hasNonCombinablePromotion = true;
    }
  };
  
  const applyPercentageOffPromotionToUnits = (promotion, units) => {
    const percent = Number(promotion.discountPercent || 0);
    if (percent <= 0) return;
  
    const label = `${percent}% off`;
  
    for (const unit of units) {
      if (!canUnitReceivePromotion(unit, promotion)) continue;
  
      const discount = roundMoney(unit.currentPrice * (percent / 100));
      addDiscountToUnit(unit, discount, promotion, label);
    }
  };
  
  const applyFixedAmountPromotionToUnits = (promotion, units) => {
    const amount = Number(promotion.discountAmount || 0);
    if (amount <= 0) return;
  
    const label = `$${amount.toFixed(2)} off`;
  
    for (const unit of units) {
      if (!canUnitReceivePromotion(unit, promotion)) continue;
  
      addDiscountToUnit(unit, amount, promotion, label);
    }
  };
  
  const buildEligibleBogoUnits = (promotion, units) => {
    return units.filter((unit) => canUnitReceivePromotion(unit, promotion));
  };
  
  const applyBogoPromotionToUnits = (promotion, units) => {
    const buyQty = Number(promotion.buyQuantity || 1);
    const getQty = Number(promotion.getQuantity || 1);
    const percentOffRaw = Number(
      promotion.discountPercent === "" ||
        promotion.discountPercent === undefined ||
        promotion.discountPercent === null
        ? 100
        : promotion.discountPercent
    );
    const percentOff = Math.max(0, Math.min(percentOffRaw, 100));
  
    if (buyQty < 1 || getQty < 1 || percentOff <= 0) return;
  
    const eligibleUnits = buildEligibleBogoUnits(promotion, units);
    const groupSize = buyQty + getQty;
  
    if (eligibleUnits.length < groupSize) return;
  
    const discountedUnitCount =
      Math.floor(eligibleUnits.length / groupSize) * getQty;
  
    if (discountedUnitCount <= 0) return;
  
    const sortedEligibleUnits = [...eligibleUnits].sort(
      (a, b) => a.currentPrice - b.currentPrice
    );
  
    const discountedUnits = sortedEligibleUnits.slice(0, discountedUnitCount);
  
    const label =
      percentOff >= 100
        ? `Buy ${buyQty}, get ${getQty} free`
        : `Buy ${buyQty}, get ${getQty} ${percentOff}% off`;
  
    for (const unit of discountedUnits) {
      const discountAmount = roundMoney(unit.currentPrice * (percentOff / 100));
      addDiscountToUnit(unit, discountAmount, promotion, label);
    }
  };
  
  const applyPromotionToUnits = (promotion, units) => {
    if (promotion.promoType === "percentage_off") {
      applyPercentageOffPromotionToUnits(promotion, units);
    } else if (promotion.promoType === "fixed_amount_off") {
      applyFixedAmountPromotionToUnits(promotion, units);
    } else if (promotion.promoType === "bogo") {
      applyBogoPromotionToUnits(promotion, units);
    }
  };
  
  const rebuildLineItemsFromUnits = (originalLineItems, units) => {
    return originalLineItems.map((line) => {
      const lineUnits = units.filter((unit) => unit.lineId === line.lineId);
  
      const lineDiscount = roundMoney(
        lineUnits.reduce((sum, unit) => sum + unit.lineDiscount, 0)
      );
  
      const discountedLineTotal = roundMoney(
        lineUnits.reduce((sum, unit) => sum + unit.currentPrice, 0)
      );
  
      const promotionMap = new Map();
      const promotionLabels = [];
  
      for (const unit of lineUnits) {
        for (const promotion of unit.appliedPromotions) {
          if (promotion?._id && !promotionMap.has(promotion._id)) {
            promotionMap.set(promotion._id, promotion);
          }
        }
  
        for (const label of unit.promotionLabels) {
          if (!promotionLabels.includes(label)) {
            promotionLabels.push(label);
          }
        }
      }
  
      return {
        ...line,
        lineDiscount,
        discountedLineTotal,
        appliedPromotions: Array.from(promotionMap.values()),
        promotionLabels,
        appliedPromotion: Array.from(promotionMap.values())[0] || null,
        promotionLabel: promotionLabels[0] || "",
      };
    });
  };
  
  const getAppliedPromotionsFromLineItems = (lineItems) => {
    const map = new Map();
  
    for (const line of lineItems) {
      for (const promotion of line.appliedPromotions || []) {
        if (promotion?._id && !map.has(promotion._id)) {
          map.set(promotion._id, promotion);
        }
      }
    }
  
    return Array.from(map.values());
  };
  
  const calculatePricingForPromotions = (cartItems, chosenPromotions) => {
    const originalLineItems = buildLineItems(cartItems);
    const units = buildUnitsFromLineItems(originalLineItems);
  
    for (const promotion of chosenPromotions) {
      applyPromotionToUnits(promotion, units);
    }
  
    const lineItems = rebuildLineItemsFromUnits(originalLineItems, units);
  
    const originalSubtotal = roundMoney(
      lineItems.reduce((sum, line) => sum + line.originalLineTotal, 0)
    );
  
    const promotionsDiscountTotal = roundMoney(
      lineItems.reduce((sum, line) => sum + line.lineDiscount, 0)
    );
  
    const finalSubtotal = roundMoney(originalSubtotal - promotionsDiscountTotal);
  
    return {
      lineItems,
      originalSubtotal,
      promotionsDiscountTotal,
      finalSubtotal,
      appliedPromotions: getAppliedPromotionsFromLineItems(lineItems),
    };
  };
  
  const getPromotionSubsets = (promotions) => {
    const subsets = [];
    const total = promotions.length;
    const count = 1 << total;
  
    for (let mask = 0; mask < count; mask++) {
      const subset = [];
  
      for (let i = 0; i < total; i++) {
        if (mask & (1 << i)) {
          subset.push(promotions[i]);
        }
      }
  
      subsets.push(subset);
    }
  
    return subsets;
  };
  
  const getBestPromotionSelection = (cartItems, activePromotions) => {
    const sorted = sortPromotions(activePromotions);
  
    // Avoid a huge brute-force explosion if promotions grow very large.
    // In that case, fall back to all combinables + best single non-combinable.
    if (sorted.length > 12) {
      const combinable = sorted.filter((p) => p.canCombine);
      const nonCombinable = sorted.filter((p) => !p.canCombine);
  
      let bestPricing = calculatePricingForPromotions(cartItems, combinable);
      let bestSelection = combinable;
  
      for (const promo of nonCombinable) {
        const testSelection = [...combinable, promo];
        const testPricing = calculatePricingForPromotions(cartItems, testSelection);
  
        if (testPricing.finalSubtotal < bestPricing.finalSubtotal) {
          bestPricing = testPricing;
          bestSelection = testSelection;
        }
      }
  
      return {
        selectedPromotions: bestSelection,
        pricing: bestPricing,
      };
    }
  
    const subsets = getPromotionSubsets(sorted);
  
    let bestSelection = [];
    let bestPricing = calculatePricingForPromotions(cartItems, []);
  
    for (const subset of subsets) {
      const pricing = calculatePricingForPromotions(cartItems, subset);
  
      if (pricing.finalSubtotal < bestPricing.finalSubtotal) {
        bestPricing = pricing;
        bestSelection = subset;
        continue;
      }
  
      if (pricing.finalSubtotal === bestPricing.finalSubtotal) {
        if (subset.length < bestSelection.length) {
          bestPricing = pricing;
          bestSelection = subset;
        }
      }
    }
  
    return {
      selectedPromotions: bestSelection,
      pricing: bestPricing,
    };
  };
  
  export function PromotionsProvider({ children }) {
    const [promotions, setPromotions] = useState([]);
    const [loadingPromotions, setLoadingPromotions] = useState(true);
    const [promotionsError, setPromotionsError] = useState("");
  
    const loadPromotions = useCallback(async () => {
      try {
        setLoadingPromotions(true);
        setPromotionsError("");
  
        const { data } = await axios.post("/api/get-promotions");
  
        if (data?.error) {
          setPromotionsError(data.error);
          setPromotions([]);
          return;
        }
  
        const loadedPromotions = Array.isArray(data?.promotions)
          ? data.promotions
          : [];
  
        setPromotions(sortPromotions(loadedPromotions));
      } catch (error) {
        setPromotionsError(
          error?.response?.data?.error || "Unable to load promotions."
        );
        setPromotions([]);
      } finally {
        setLoadingPromotions(false);
      }
    }, []);
  
    useEffect(() => {
      loadPromotions();
    }, [loadPromotions]);
  
    const activePromotions = useMemo(() => {
      return sortPromotions(promotions.filter(isPromotionCurrentlyValid));
    }, [promotions]);
  
    const getApplicablePromotionsForProduct = useCallback(
      (product) => {
        return activePromotions.filter((promotion) =>
          promotionAppliesToProduct(promotion, product)
        );
      },
      [activePromotions]
    );
  
    const getProductDisplayPricing = useCallback(
      (product, options = {}) => {
        const { selectedPromotionIds = null, useAutomaticBestDeal = true } = options;
  
        const applicablePromotions = activePromotions.filter((promotion) =>
          promotionAppliesToProduct(promotion, product)
        );
  
        const chosenPromotions =
          useAutomaticBestDeal
            ? getBestPromotionSelection([{ ...product, quantity: 1 }], applicablePromotions)
                .selectedPromotions
            : applicablePromotions.filter((promotion) =>
                Array.isArray(selectedPromotionIds)
                  ? selectedPromotionIds.includes(promotion._id)
                  : true
              );
  
        const pricing = calculatePricingForPromotions(
          [{ ...product, quantity: 1 }],
          chosenPromotions
        );
  
        const line = pricing.lineItems[0];
  
        return {
          originalPrice: line.originalLineTotal,
          finalPrice: line.discountedLineTotal,
          discountAmount: line.lineDiscount,
          appliedPromotions: line.appliedPromotions,
          promotionLabel: line.promotionLabel || "",
          promotionLabels: line.promotionLabels || [],
          hasDiscount: line.lineDiscount > 0,
        };
      },
      [activePromotions]
    );
  
    const getCartPricing = useCallback(
      (
        cartItems,
        options = {
          couponState: null,
          useAutomaticBestDeal: true,
          selectedPromotionIds: [],
        }
      ) => {
        const {
          couponState = null,
          useAutomaticBestDeal = true,
          selectedPromotionIds = [],
        } = options || {};
  
        const availablePromotions = activePromotions.filter((promotion) => {
          return normalizeCartItems(cartItems).some((item) =>
            promotionAppliesToProduct(promotion, item)
          );
        });
  
        const chosenPromotions = useAutomaticBestDeal
          ? getBestPromotionSelection(cartItems, availablePromotions).selectedPromotions
          : availablePromotions.filter((promotion) =>
              selectedPromotionIds.includes(promotion._id)
            );
  
        const pricing = calculatePricingForPromotions(cartItems, chosenPromotions);
  
        const couponDiscountTotal = 0;
        const appliedCoupon = couponState || null;
  
        const finalSubtotal = roundMoney(
          pricing.finalSubtotal - couponDiscountTotal
        );
  
        return {
          lineItems: pricing.lineItems,
          originalSubtotal: pricing.originalSubtotal,
          promotionsDiscountTotal: pricing.promotionsDiscountTotal,
          couponDiscountTotal,
          discountedSubtotal: finalSubtotal,
          finalSubtotal,
          appliedPromotions: pricing.appliedPromotions,
          appliedCoupon,
          availablePromotions,
          selectedPromotionIds: chosenPromotions.map((promotion) => promotion._id),
          useAutomaticBestDeal,
        };
      },
      [activePromotions]
    );
  
    const value = useMemo(
      () => ({
        promotions,
        activePromotions,
        loadingPromotions,
        promotionsError,
        reloadPromotions: loadPromotions,
        getApplicablePromotionsForProduct,
        getProductDisplayPricing,
        getCartPricing,
      }),
      [
        promotions,
        activePromotions,
        loadingPromotions,
        promotionsError,
        loadPromotions,
        getApplicablePromotionsForProduct,
        getProductDisplayPricing,
        getCartPricing,
      ]
    );
  
    return (
      <PromotionsContext.Provider value={value}>
        {children}
      </PromotionsContext.Provider>
    );
  }
  
  export function usePromotions() {
    const context = useContext(PromotionsContext);
  
    if (!context) {
      throw new Error("usePromotions must be used within a PromotionsProvider");
    }
  
    return context;
  }

// import React, {
//     createContext,
//     useCallback,
//     useContext,
//     useEffect,
//     useMemo,
//     useState,
//   } from "react";
//   import axios from "axios";
  
//   const PromotionsContext = createContext(null);
  
//   const roundMoney = (value) =>
//     Math.round((Number(value) + Number.EPSILON) * 100) / 100;
  
//   const nowIsWithinRange = (promotion) => {
//     const now = new Date();
  
//     const startOk =
//       !promotion.startDate ||
//       new Date(promotion.startDate).getTime() <= now.getTime();
  
//     const endOk =
//       !promotion.endDate ||
//       new Date(promotion.endDate).getTime() >= now.getTime();
  
//     return startOk && endOk;
//   };
  
//   const isPromotionCurrentlyValid = (promotion) => {
//     return Boolean(promotion?.isActive && nowIsWithinRange(promotion));
//   };
  
//   const sortPromotions = (promotions) => {
//     return [...promotions].sort((a, b) => {
//       const priorityA = Number(a.priority || 0);
//       const priorityB = Number(b.priority || 0);
  
//       if (priorityA !== priorityB) return priorityB - priorityA;
  
//       const createdA = new Date(a.createdAt || 0).getTime();
//       const createdB = new Date(b.createdAt || 0).getTime();
  
//       return createdB - createdA;
//     });
//   };
  
//   const normalizeCartItems = (cartItems) => {
//     return (cartItems || []).map((item, index) => ({
//       ...item,
//       __cartLineId:
//         item.__cartLineId ||
//         `${item.SKU || "unknown"}-${index}-${item.productURI || "item"}`,
//       quantity: Number(item.quantity || 1),
//       price: Number(item.price || 0),
//     }));
//   };
  
//   const promotionAppliesToProduct = (promotion, product) => {
//     if (!promotion || !product) return false;
//     if (!isPromotionCurrentlyValid(promotion)) return false;
  
//     if (promotion.scopeType === "global") return true;
  
//     if (promotion.scopeType === "products") {
//       const sku = String(product.SKU);
//       return Array.isArray(promotion.productSKUs)
//         ? promotion.productSKUs.map(String).includes(sku)
//         : false;
//     }
  
//     return false;
//   };
  
//   const buildInitialLineItems = (cartItems) => {
//     return normalizeCartItems(cartItems).map((item) => ({
//       lineId: item.__cartLineId,
//       item,
//       quantity: item.quantity,
//       basePrice: Number(item.price || 0),
//       originalLineTotal: roundMoney(
//         Number(item.price || 0) * Number(item.quantity || 1)
//       ),
//       discountedLineTotal: roundMoney(
//         Number(item.price || 0) * Number(item.quantity || 1)
//       ),
//       lineDiscount: 0,
//       appliedPromotions: [],
//       promotionLabels: [],
//     }));
//   };
  
//   const addDiscountToLine = (line, amount, promotion, label) => {
//     const safeAmount = roundMoney(
//       Math.max(0, Math.min(amount, line.discountedLineTotal))
//     );
  
//     if (safeAmount <= 0) return;
  
//     line.lineDiscount = roundMoney(line.lineDiscount + safeAmount);
//     line.discountedLineTotal = roundMoney(line.discountedLineTotal - safeAmount);
  
//     if (
//       promotion?._id &&
//       !line.appliedPromotions.find((p) => p._id === promotion._id)
//     ) {
//       line.appliedPromotions.push(promotion);
//     }
  
//     if (label && !line.promotionLabels.includes(label)) {
//       line.promotionLabels.push(label);
//     }
//   };
  
//   const applyPercentageOffPromotion = (promotion, lineItems) => {
//     const percent = Number(promotion.discountPercent || 0);
//     if (percent <= 0) return;
  
//     for (const line of lineItems) {
//       if (!promotionAppliesToProduct(promotion, line.item)) continue;
//       if (line.lineDiscount > 0) continue;
  
//       const discount = roundMoney(line.originalLineTotal * (percent / 100));
//       addDiscountToLine(line, discount, promotion, `${percent}% off`);
//     }
//   };
  
//   const applyFixedAmountPromotion = (promotion, lineItems) => {
//     const amount = Number(promotion.discountAmount || 0);
//     if (amount <= 0) return;
  
//     for (const line of lineItems) {
//       if (!promotionAppliesToProduct(promotion, line.item)) continue;
//       if (line.lineDiscount > 0) continue;
  
//       const discount = roundMoney(amount * line.quantity);
//       addDiscountToLine(line, discount, promotion, `$${amount.toFixed(2)} off`);
//     }
//   };
  
//   const buildEligibleBogoUnits = (promotion, lineItems) => {
//     const units = [];
  
//     for (const line of lineItems) {
//       if (!promotionAppliesToProduct(promotion, line.item)) continue;
  
//       for (let i = 0; i < line.quantity; i++) {
//         units.push({
//           lineId: line.lineId,
//           price: line.basePrice,
//         });
//       }
//     }
  
//     return units;
//   };
  
//   const applyBogoPromotion = (promotion, lineItems) => {
//     const buyQty = Number(promotion.buyQuantity || 1);
//     const getQty = Number(promotion.getQuantity || 1);
//     const percentOffRaw = Number(promotion.discountPercent || 100);
//     const percentOff = Math.max(0, Math.min(percentOffRaw, 100));
  
//     if (buyQty < 1 || getQty < 1) return;
//     if (percentOff <= 0) return;
  
//     const eligibleUnits = buildEligibleBogoUnits(promotion, lineItems);
//     const groupSize = buyQty + getQty;
  
//     if (eligibleUnits.length < groupSize) return;
  
//     const sortedUnits = [...eligibleUnits].sort((a, b) => a.price - b.price);
  
//     const discountedUnitCount =
//       Math.floor(eligibleUnits.length / groupSize) * getQty;
  
//     if (discountedUnitCount <= 0) return;
  
//     const discountedUnits = sortedUnits.slice(0, discountedUnitCount);
  
//     const label =
//       percentOff === 100
//         ? `Buy ${buyQty}, get ${getQty}`
//         : `Buy ${buyQty}, get ${getQty} ${percentOff}% off`;
  
//     for (const discountedUnit of discountedUnits) {
//       const line = lineItems.find((entry) => entry.lineId === discountedUnit.lineId);
//       if (!line) continue;
  
//       const discountAmount = roundMoney(
//         discountedUnit.price * (percentOff / 100)
//       );
  
//       addDiscountToLine(line, discountAmount, promotion, label);
//     }
//   };
  
//   const applyPromotionsToLineItems = (promotions, lineItems) => {
//     const sortedPromotions = sortPromotions(promotions);
  
//     for (const promotion of sortedPromotions) {
//       if (!isPromotionCurrentlyValid(promotion)) continue;
  
//       if (promotion.promoType === "percentage_off") {
//         applyPercentageOffPromotion(promotion, lineItems);
//       } else if (promotion.promoType === "fixed_amount_off") {
//         applyFixedAmountPromotion(promotion, lineItems);
//       } else if (promotion.promoType === "bogo") {
//         applyBogoPromotion(promotion, lineItems);
//       }
//     }
  
//     return lineItems;
//   };
  
//   export function PromotionsProvider({ children }) {
//     const [promotions, setPromotions] = useState([]);
//     const [loadingPromotions, setLoadingPromotions] = useState(true);
//     const [promotionsError, setPromotionsError] = useState("");
  
//     const loadPromotions = useCallback(async () => {
//       try {
//         setLoadingPromotions(true);
//         setPromotionsError("");
  
//         const { data } = await axios.post("/api/get-promotions");
  
//         if (data?.error) {
//           setPromotionsError(data.error);
//           setPromotions([]);
//           return;
//         }
  
//         const loadedPromotions = Array.isArray(data?.promotions)
//           ? data.promotions
//           : [];
  
//         setPromotions(sortPromotions(loadedPromotions));
//       } catch (error) {
//         setPromotionsError(
//           error?.response?.data?.error || "Unable to load promotions."
//         );
//         setPromotions([]);
//       } finally {
//         setLoadingPromotions(false);
//       }
//     }, []);
  
//     useEffect(() => {
//       loadPromotions();
//     }, [loadPromotions]);
  
//     const activePromotions = useMemo(() => {
//       return sortPromotions(promotions.filter(isPromotionCurrentlyValid));
//     }, [promotions]);
  
//     const getApplicablePromotionsForProduct = useCallback(
//       (product) => {
//         return activePromotions.filter((promotion) =>
//           promotionAppliesToProduct(promotion, product)
//         );
//       },
//       [activePromotions]
//     );
  
//     const getProductDisplayPricing = useCallback(
//       (product) => {
//         const lineItems = buildInitialLineItems([{ ...product, quantity: 1 }]);
//         applyPromotionsToLineItems(activePromotions, lineItems);
  
//         const line = lineItems[0];
  
//         return {
//           originalPrice: line.originalLineTotal,
//           finalPrice: line.discountedLineTotal,
//           discountAmount: line.lineDiscount,
//           appliedPromotions: line.appliedPromotions,
//           promotionLabel: line.promotionLabels[0] || "",
//           promotionLabels: line.promotionLabels,
//           hasDiscount: line.lineDiscount > 0,
//         };
//       },
//       [activePromotions]
//     );
  
//     const getCartPricing = useCallback(
//       (cartItems, couponState = null) => {
//         const lineItems = buildInitialLineItems(cartItems);
  
//         applyPromotionsToLineItems(activePromotions, lineItems);
  
//         const originalSubtotal = roundMoney(
//           lineItems.reduce((sum, line) => sum + line.originalLineTotal, 0)
//         );
  
//         const promotionsDiscountTotal = roundMoney(
//           lineItems.reduce((sum, line) => sum + line.lineDiscount, 0)
//         );
  
//         const couponDiscountTotal = 0;
//         const appliedCoupon = couponState || null;
  
//         const finalSubtotal = roundMoney(
//           originalSubtotal - promotionsDiscountTotal - couponDiscountTotal
//         );
  
//         const appliedPromotionsMap = new Map();
  
//         for (const line of lineItems) {
//           for (const promotion of line.appliedPromotions) {
//             if (promotion?._id && !appliedPromotionsMap.has(promotion._id)) {
//               appliedPromotionsMap.set(promotion._id, promotion);
//             }
//           }
//         }
  
//         return {
//           lineItems: lineItems.map((line) => ({
//             ...line,
//             appliedPromotion: line.appliedPromotions[0] || null,
//             promotionLabel: line.promotionLabels[0] || "",
//           })),
//           originalSubtotal,
//           promotionsDiscountTotal,
//           couponDiscountTotal,
//           discountedSubtotal: finalSubtotal,
//           finalSubtotal,
//           appliedPromotions: Array.from(appliedPromotionsMap.values()),
//           appliedCoupon,
//         };
//       },
//       [activePromotions]
//     );
  
//     const value = useMemo(
//       () => ({
//         promotions,
//         activePromotions,
//         loadingPromotions,
//         promotionsError,
//         reloadPromotions: loadPromotions,
//         getApplicablePromotionsForProduct,
//         getProductDisplayPricing,
//         getCartPricing,
//       }),
//       [
//         promotions,
//         activePromotions,
//         loadingPromotions,
//         promotionsError,
//         loadPromotions,
//         getApplicablePromotionsForProduct,
//         getProductDisplayPricing,
//         getCartPricing,
//       ]
//     );
  
//     return (
//       <PromotionsContext.Provider value={value}>
//         {children}
//       </PromotionsContext.Provider>
//     );
//   }
  
//   export function usePromotions() {
//     const context = useContext(PromotionsContext);
  
//     if (!context) {
//       throw new Error("usePromotions must be used within a PromotionsProvider");
//     }
  
//     return context;
//   }

// import React, {
//     createContext,
//     useCallback,
//     useContext,
//     useEffect,
//     useMemo,
//     useState,
//   } from "react";
//   import axios from "axios";
  
//   const PromotionsContext = createContext(null);
  
//   const roundMoney = (value) =>
//     Math.round((Number(value) + Number.EPSILON) * 100) / 100;
  
//   const nowIsWithinRange = (promotion) => {
//     const now = new Date();
  
//     const startOk =
//       !promotion.startDate || new Date(promotion.startDate).getTime() <= now.getTime();
  
//     const endOk =
//       !promotion.endDate || new Date(promotion.endDate).getTime() >= now.getTime();
  
//     return startOk && endOk;
//   };
  
//   const isPromotionCurrentlyValid = (promotion) => {
//     return Boolean(promotion?.isActive && nowIsWithinRange(promotion));
//   };
  
//   const sortPromotions = (promotions) => {
//     return [...promotions].sort((a, b) => {
//       const priorityA = Number(a.priority || 0);
//       const priorityB = Number(b.priority || 0);
  
//       if (priorityA !== priorityB) return priorityB - priorityA;
  
//       const createdA = new Date(a.createdAt || 0).getTime();
//       const createdB = new Date(b.createdAt || 0).getTime();
  
//       return createdB - createdA;
//     });
//   };
  
//   const normalizeCartItems = (cartItems) => {
//     return (cartItems || []).map((item, index) => ({
//       ...item,
//       __cartLineId:
//         item.__cartLineId ||
//         `${item.SKU || "unknown"}-${index}-${item.productURI || "item"}`,
//       quantity: Number(item.quantity || 1),
//       price: Number(item.price || 0),
//     }));
//   };
  
//   const promotionAppliesToProduct = (promotion, product) => {
//     if (!promotion || !product) return false;
//     if (!isPromotionCurrentlyValid(promotion)) return false;
  
//     if (promotion.scopeType === "global") return true;
  
//     if (promotion.scopeType === "products") {
//       const sku = String(product.SKU);
//       return Array.isArray(promotion.productSKUs)
//         ? promotion.productSKUs.map(String).includes(sku)
//         : false;
//     }
  
//     return false;
//   };
  
//   const buildInitialLineItems = (cartItems) => {
//     return normalizeCartItems(cartItems).map((item) => ({
//       lineId: item.__cartLineId,
//       item,
//       quantity: item.quantity,
//       basePrice: Number(item.price || 0),
//       originalLineTotal: roundMoney(Number(item.price || 0) * Number(item.quantity || 1)),
//       discountedLineTotal: roundMoney(Number(item.price || 0) * Number(item.quantity || 1)),
//       lineDiscount: 0,
//       appliedPromotions: [],
//       promotionLabels: [],
//     }));
//   };
  
//   const addDiscountToLine = (line, amount, promotion, label) => {
//     const safeAmount = roundMoney(
//       Math.max(0, Math.min(amount, line.discountedLineTotal))
//     );
  
//     if (safeAmount <= 0) return;
  
//     line.lineDiscount = roundMoney(line.lineDiscount + safeAmount);
//     line.discountedLineTotal = roundMoney(line.discountedLineTotal - safeAmount);
  
//     if (promotion?._id && !line.appliedPromotions.find((p) => p._id === promotion._id)) {
//       line.appliedPromotions.push(promotion);
//     }
  
//     if (label && !line.promotionLabels.includes(label)) {
//       line.promotionLabels.push(label);
//     }
//   };
  
//   const applyPercentageOffPromotion = (promotion, lineItems) => {
//     const percent = Number(promotion.discountPercent || 0);
//     if (percent <= 0) return;
  
//     for (const line of lineItems) {
//       if (!promotionAppliesToProduct(promotion, line.item)) continue;
//       if (line.lineDiscount > 0) continue;
  
//       const discount = roundMoney(line.originalLineTotal * (percent / 100));
//       addDiscountToLine(line, discount, promotion, `${percent}% off`);
//     }
//   };
  
//   const applyFixedAmountPromotion = (promotion, lineItems) => {
//     const amount = Number(promotion.discountAmount || 0);
//     if (amount <= 0) return;
  
//     for (const line of lineItems) {
//       if (!promotionAppliesToProduct(promotion, line.item)) continue;
//       if (line.lineDiscount > 0) continue;
  
//       const discount = roundMoney(amount * line.quantity);
//       addDiscountToLine(line, discount, promotion, `$${amount.toFixed(2)} off`);
//     }
//   };
  
//   const buildEligibleBogoUnits = (promotion, lineItems) => {
//     const units = [];
  
//     for (const line of lineItems) {
//       if (!promotionAppliesToProduct(promotion, line.item)) continue;
  
//       for (let i = 0; i < line.quantity; i++) {
//         units.push({
//           lineId: line.lineId,
//           price: line.basePrice,
//         });
//       }
//     }
  
//     return units;
//   };
  
//   const applyBogoPromotion = (promotion, lineItems) => {
//     const buyQty = Number(promotion.buyQuantity || 1);
//     const getQty = Number(promotion.getQuantity || 1);
  
//     if (buyQty < 1 || getQty < 1) return;
  
//     const eligibleUnits = buildEligibleBogoUnits(promotion, lineItems);
//     const groupSize = buyQty + getQty;
  
//     if (eligibleUnits.length < groupSize) return;
  
//     // Sort cheapest first so the "free" items are the lowest-priced eligible units.
//     // This is a safe/default way to apply sitewide or scoped BOGO promotions.
//     const sortedUnits = [...eligibleUnits].sort((a, b) => a.price - b.price);
  
//     const freeUnitCount =
//       Math.floor(eligibleUnits.length / groupSize) * getQty;
  
//     if (freeUnitCount <= 0) return;
  
//     const freeUnits = sortedUnits.slice(0, freeUnitCount);
  
//     const label = `Buy ${buyQty}, get ${getQty}`;
  
//     for (const freeUnit of freeUnits) {
//       const line = lineItems.find((entry) => entry.lineId === freeUnit.lineId);
//       if (!line) continue;
  
//       addDiscountToLine(line, freeUnit.price, promotion, label);
//     }
//   };
  
//   const applyPromotionsToLineItems = (promotions, lineItems) => {
//     const sortedPromotions = sortPromotions(promotions);
  
//     for (const promotion of sortedPromotions) {
//       if (!isPromotionCurrentlyValid(promotion)) continue;
  
//       if (promotion.promoType === "percentage_off") {
//         applyPercentageOffPromotion(promotion, lineItems);
//       } else if (promotion.promoType === "fixed_amount_off") {
//         applyFixedAmountPromotion(promotion, lineItems);
//       } else if (promotion.promoType === "bogo") {
//         applyBogoPromotion(promotion, lineItems);
//       }
//     }
  
//     return lineItems;
//   };
  
//   export function PromotionsProvider({ children }) {
//     const [promotions, setPromotions] = useState([]);
//     const [loadingPromotions, setLoadingPromotions] = useState(true);
//     const [promotionsError, setPromotionsError] = useState("");
  
//     const loadPromotions = useCallback(async () => {
//       try {
//         setLoadingPromotions(true);
//         setPromotionsError("");
  
//         const { data } = await axios.post("/api/get-promotions");
  
//         if (data?.error) {
//           setPromotionsError(data.error);
//           setPromotions([]);
//           return;
//         }
  
//         const loadedPromotions = Array.isArray(data?.promotions)
//           ? data.promotions
//           : [];
  
//         setPromotions(sortPromotions(loadedPromotions));
//       } catch (error) {
//         setPromotionsError(
//           error?.response?.data?.error || "Unable to load promotions."
//         );
//         setPromotions([]);
//       } finally {
//         setLoadingPromotions(false);
//       }
//     }, []);
  
//     useEffect(() => {
//       loadPromotions();
//     }, [loadPromotions]);
  
//     const activePromotions = useMemo(() => {
//       return sortPromotions(promotions.filter(isPromotionCurrentlyValid));
//     }, [promotions]);
  
//     const getApplicablePromotionsForProduct = useCallback(
//       (product) => {
//         return activePromotions.filter((promotion) =>
//           promotionAppliesToProduct(promotion, product)
//         );
//       },
//       [activePromotions]
//     );
  
//     const getProductDisplayPricing = useCallback(
//       (product) => {
//         const lineItems = buildInitialLineItems([{ ...product, quantity: 1 }]);
//         applyPromotionsToLineItems(activePromotions, lineItems);
  
//         const line = lineItems[0];
  
//         return {
//           originalPrice: line.originalLineTotal,
//           finalPrice: line.discountedLineTotal,
//           discountAmount: line.lineDiscount,
//           appliedPromotions: line.appliedPromotions,
//           promotionLabel: line.promotionLabels[0] || "",
//           promotionLabels: line.promotionLabels,
//           hasDiscount: line.lineDiscount > 0,
//         };
//       },
//       [activePromotions]
//     );
  
//     const getCartPricing = useCallback(
//       (cartItems, couponState = null) => {
//         const lineItems = buildInitialLineItems(cartItems);
  
//         applyPromotionsToLineItems(activePromotions, lineItems);
  
//         const originalSubtotal = roundMoney(
//           lineItems.reduce((sum, line) => sum + line.originalLineTotal, 0)
//         );
  
//         const promotionsDiscountTotal = roundMoney(
//           lineItems.reduce((sum, line) => sum + line.lineDiscount, 0)
//         );
  
//         // Placeholder for future coupon support
//         const couponDiscountTotal = 0;
//         const appliedCoupon = couponState || null;
  
//         const finalSubtotal = roundMoney(
//           originalSubtotal - promotionsDiscountTotal - couponDiscountTotal
//         );
  
//         const appliedPromotionsMap = new Map();
  
//         for (const line of lineItems) {
//           for (const promotion of line.appliedPromotions) {
//             if (promotion?._id && !appliedPromotionsMap.has(promotion._id)) {
//               appliedPromotionsMap.set(promotion._id, promotion);
//             }
//           }
//         }
  
//         return {
//           lineItems: lineItems.map((line) => ({
//             ...line,
//             appliedPromotion: line.appliedPromotions[0] || null,
//             promotionLabel: line.promotionLabels[0] || "",
//           })),
//           originalSubtotal,
//           promotionsDiscountTotal,
//           couponDiscountTotal,
//           discountedSubtotal: finalSubtotal,
//           finalSubtotal,
//           appliedPromotions: Array.from(appliedPromotionsMap.values()),
//           appliedCoupon,
//         };
//       },
//       [activePromotions]
//     );
  
//     const value = useMemo(
//       () => ({
//         promotions,
//         activePromotions,
//         loadingPromotions,
//         promotionsError,
//         reloadPromotions: loadPromotions,
//         getApplicablePromotionsForProduct,
//         getProductDisplayPricing,
//         getCartPricing,
//       }),
//       [
//         promotions,
//         activePromotions,
//         loadingPromotions,
//         promotionsError,
//         loadPromotions,
//         getApplicablePromotionsForProduct,
//         getProductDisplayPricing,
//         getCartPricing,
//       ]
//     );
  
//     return (
//       <PromotionsContext.Provider value={value}>
//         {children}
//       </PromotionsContext.Provider>
//     );
//   }
  
//   export function usePromotions() {
//     const context = useContext(PromotionsContext);
  
//     if (!context) {
//       throw new Error("usePromotions must be used within a PromotionsProvider");
//     }
  
//     return context;
//   }